<template>
  <Modal
    title="Welcome!"
    :modal-buttons="modalButtons"
    :show-cancel-button="false"
    :after-closed="onModalClosed"
  >
    <template v-if="!isReady">
      <p>
        Adding
        <strong>{{ orgName || "the organization" }}</strong> to your account…
        This may take a few seconds. You can safely close this dialog.
      </p>
      <div class="spinner-row">
        <Spinner />
      </div>
    </template>
    <template v-else>
      <p v-if="orgName">
        You're all set — welcome to <strong>{{ orgName }}</strong
        >.
      </p>
      <p v-else>You're all set — welcome to the organization.</p>
      <p class="hint">
        The organization has been pinned to the menu for easy access.
      </p>
    </template>
  </Modal>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  ref,
  shallowRef,
} from "vue";

import type { Controller } from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import Modal, { type ModalButtons } from "@/components/modals/Modal.vue";
import Spinner from "@/components/Spinner.vue";

type OrganizationRef = Awaited<ReturnType<Controller["organization"]["get"]>>;

/**
 * Welcome modal shown after a user accepts an org invitation.
 *
 * Membership is now provisioned by an asynchronous backend job (see
 * api@c1f351161 "Move organization invitations to a job"), so when this
 * modal opens the user is not necessarily a member of the org yet. The
 * modal therefore renders in two states:
 *
 *  - **pending** (default): "Adding … to your account" with a spinner and
 *    a disabled `Open` button. The dialog is dismissable.
 *  - **ready**: success copy with the `Open` button enabled.
 *
 * The transition is driven by the `ready` promise, which the caller
 * resolves once `controller.organization.all()` reports membership. The
 * caller (the post-setup queue) is also responsible for pinning the org
 * homepage at that moment, mirroring the create-organization flow.
 *
 * `controller.organization.get(orgId)` is subscribed reactively so the
 * org name fills in as soon as data replicates, in either state.
 */
export default defineComponent({
  components: { Modal, Spinner },
  props: {
    orgId: { type: String, required: true },
    /** Resolves when the org is actually a member (queue tracks this). */
    ready: {
      type: Object as PropType<Promise<void>>,
      required: true,
    },
    /** Forwarded to `Modal`; `useModal` callers inject this from `attrs`. */
    afterClosed: { type: Function as PropType<() => void>, required: false },
  },
  emits: {
    close: () => true,
    open: () => true,
  },
  setup(props, { emit }) {
    const controller = inject.controller();
    const logger = inject.logger("OrganizationJoinedModal");

    const isReady = ref(false);
    void props.ready.then(() => {
      isReady.value = true;
    });

    const orgRef = shallowRef<OrganizationRef | null>(null);
    const orgName = computed(() => orgRef.value?.value?.name ?? "");

    let cancelled = false;
    let userChoseOpen = false;

    void (async () => {
      try {
        const r = await controller.organization.get(props.orgId);
        if (cancelled) {
          r.release();
          return;
        }
        orgRef.value = r;
      } catch (e) {
        logger.error("Failed to subscribe to organization", e);
      }
    })();

    onBeforeUnmount(() => {
      cancelled = true;
      orgRef.value?.release();
    });

    // While the org is still being added, surface an active "Close" button
    // (dismissing the dialog emits `close` like Esc / X / outside click). Once
    // membership lands the button morphs into the primary "Open" action that
    // navigates to the org screen via `userChoseOpen` → `onModalClosed`.
    const modalButtons = computed<ModalButtons>(() =>
      isReady.value
        ? [
            {
              title: "Open",
              kind: "main",
              type: "primary",
              func: async () => {
                userChoseOpen = true;
                // Returning undefined → Modal closes itself via its slot.close.
              },
            },
          ]
        : [
            {
              title: "Close",
              kind: "main",
              type: "primary",
              func: async () => {
                // userChoseOpen stays false → `onModalClosed` emits `close`.
              },
            },
          ]
    );

    const onModalClosed = () => {
      // Fires after the close animation completes, regardless of cause
      // (button action, Esc, click outside, X). Distinguish by the flag
      // we set in the Open button's handler.
      if (userChoseOpen) {
        emit("open");
      } else {
        emit("close");
      }
      props.afterClosed?.();
    };

    return { isReady, orgName, modalButtons, onModalClosed };
  },
});
</script>

<style scoped lang="scss">
.spinner-row {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.hint {
  margin-top: 8px;

  @include themed() {
    color: t($grey-60);
  }
}
</style>
